import React, { useEffect } from "react";
import { Provider, useDispatch } from "react-redux";
import { I18nextProvider } from "react-i18next";
import i18n from "i18n/i18n";
import store from "./redux/store";
import GlobalStylesComponent from "components/GlobalStyles/GlobalStylesComponent";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import Home from "pages/main/IndexPage/IndexPage";
import SearchPage from "pages/main/SearchPage/SearchPage";
import CatalogDetailPage from "pages/main/CatalogDetailPage/CatalogDetailPage";
import default_theme from "assets/themes/themes";
import SolutionsPage from "pages/main/SolutionCatalogPage/SolutionsPage";
import RuralDevelopmentPage from "pages/main/SolutionCatalogPage/RuralDevelopmentPage";
import WizardPage from "pages/main/WizardPage/WizardPage";
import WorkspacesPage from "pages/main/WorkspacesPage/WorkspacesPage";
import ProjectDetailPage from "pages/main/ProjectDetailPage/ProjectDetailPage";
import ProjectDynamoPage from "pages/main/ProjectDynamoPage/ProjectDynamoPage";
import ProjectSettingsPage from "pages/main/ProjectSettingsPage/ProjectSettingsPage";
import AcceptProjectSharePage from "pages/main/AcceptProjectSharePage/AcceptProjectSharePage";
import LAPPage from "pages/main/LAPPage/LAPPage";
import LAPDetailPage from "pages/main/LAPDetailPage/LAPDetailPage";
import DynamoDetailPage from "pages/main/DynamoDetailPage/DynamoDetailPage";
import MainLayout from "./components/MainLayout/MainLayout";
import AuthLayout from "./components/Auth/AuthLayout";
import LoginPage from "./components/Auth/LoginPage";
import RegisterPage from "./components/Auth/RegisterPage";
import ForgotPasswordPage from "./components/Auth/ForgotPasswordPage";
import ProfilePage from "./components/Auth/ProfilePage";
import UserManagementPage from "./components/Admin/UserManagementPage";
import InvitationPage from "./components/Auth/InvitationPage";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import PartialProtectedRoute from "./components/Auth/PartialProtectedRoute";
import { checkAuth, refreshTokenRequest} from "./redux/auth/authSlice";
import StepZeroPage from "pages/main/Steps/StepZero/StepZeroPage";
import StepOnePage from "pages/main/Steps/StepOne/StepOnePage";
import StepTwoPage from 'pages/main/Steps/StepTwo/StepTwoPage';
import StepThreePage from "pages/main/Steps/StepThree/StepThreePage";
import tokenManager from "./utils/tokenManager";
import CassandraLAPDetailPage from "pages/main/LAPDetailPage/CassandraLAPDetailPage";
import LegalIndex from "components/Legal/LegalIndex";
import TermsOfUse from "components/Legal/TermsOfUse";
import PrivacyNotice from "components/Legal/PrivacyNotice";
import CookiePolicy from "components/Legal/CookiePolicy";
import AccessibilityStatement from "components/Legal/AccessibilityStatement";

// Component to initialize auth state
const AppInitializer = ({ children }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Check authentication state from localStorage on app load
    dispatch(checkAuth());
    
    const interval = setInterval(() => {
      if (tokenManager.hasTokens()) {
        const expiresAt = tokenManager.getExpiresAt();
        const now = Date.now();
        const timeLeft = expiresAt - now;

        // If less than 2 minutes remain (120,000 milliseconds)
        if (expiresAt && timeLeft > 0 && timeLeft < 120_000) {
          console.log("Token expiring soon. Dispatching silent refresh...");
          dispatch(refreshTokenRequest());
        }
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [dispatch]);

  return <>{children}</>;
};

const AppRoutes = () => (
  <Routes>
    {/* Public routes with AuthLayout (only Header and Footer) */}
    <Route
      path="/login"
      element={
        <AuthLayout>
          <LoginPage />
        </AuthLayout>
      }
    />
    <Route
      path="/register"
      element={
        <AuthLayout>
          <RegisterPage />
        </AuthLayout>
      }
    />
    <Route
      path="/forgot-password"
      element={
        <AuthLayout>
          <ForgotPasswordPage />
        </AuthLayout>
      }
    />
    <Route
      path="/invitations/accept"
      element={
        <AuthLayout>
          <InvitationPage action="accept" />
        </AuthLayout>
      }
    />
    <Route
      path="/invitations/deny"
      element={
        <AuthLayout>
          <InvitationPage action="deny" />
        </AuthLayout>
      }
    />

    {/* Public routes with MainLayout */}
    <Route
      path="/"
      element={
        <MainLayout>
          <Home />
        </MainLayout>
      }
    />
    {/* Public legal pages with MainLayout (accessible when signed out) */}
    <Route
      path="/legal"
      element={
        <MainLayout>
          <LegalIndex />
        </MainLayout>
      }
    />
    <Route
      path="/terms"
      element={
        <MainLayout>
          <TermsOfUse />
        </MainLayout>
      }
    />
    <Route
      path="/privacy"
      element={
        <MainLayout>
          <PrivacyNotice />
        </MainLayout>
      }
    />
    <Route
      path="/cookies"
      element={
        <MainLayout>
          <CookiePolicy />
        </MainLayout>
      }
    />
    <Route
      path="/accessibility"
      element={
        <MainLayout>
          <AccessibilityStatement />
        </MainLayout>
      }
    />
    {/* Partially protected routes with MainLayout (viewable but restricted for unauthenticated users) */}
    <Route
      path="/solutions"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <SolutionsPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <RuralDevelopmentPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development/solutions-catalog"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <SearchPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development/solutions-catalog/:entryId"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <CatalogDetailPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development/lap-catalog"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <LAPPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development/lap-dynamo/:dynamoCode"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <DynamoDetailPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development/lap-dynamo/:dynamoCode/lap-catalog/:lapId"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <LAPDetailPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development/lap-catalog/:lapId"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <LAPDetailPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/solutions/rural-development/lap-details/:lapId"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <CassandraLAPDetailPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/wizard"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <WizardPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/workspaces"
      element={
        <MainLayout>
          <PartialProtectedRoute>
            <WorkspacesPage />
          </PartialProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/workspaces/projects/:projectId"
      element={
        <MainLayout>
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/workspaces/projects/:projectId/dynamos/:dynamoId"
      element={
        <MainLayout>
          <ProtectedRoute>
            <ProjectDynamoPage />
          </ProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/workspaces/projects/:projectId/settings"
      element={
        <MainLayout>
          <ProtectedRoute>
            <ProjectSettingsPage />
          </ProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/projects/accept"
      element={
        <MainLayout>
          <ProtectedRoute>
            <AcceptProjectSharePage />
          </ProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/workspaces/projects/:projectId/step-0"
      element={
        <MainLayout>
          <ProtectedRoute>
            <StepZeroPage />
          </ProtectedRoute>
        </MainLayout>
      }
    />

    <Route
      path="/workspaces/projects/:projectId/step-1"
      element={
        <MainLayout>
          <ProtectedRoute>
            <StepOnePage />
          </ProtectedRoute>
        </MainLayout>
      }
    />



    

    <Route
      path="/workspaces/projects/:projectId/step-2"
      element={
        <MainLayout>
          <ProtectedRoute>
            <StepTwoPage />
          </ProtectedRoute>
        </MainLayout>
      }
    />

    <Route
      path="/workspaces/projects/:projectId/step-3"
      element={
        <MainLayout>
          <ProtectedRoute>
            <StepThreePage />
          </ProtectedRoute>
        </MainLayout>
      }
    /> 
    <Route
      path="/profile"
      element={
        <MainLayout>
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        </MainLayout>
      }
    />
    <Route
      path="/organization/users"
      element={
        <MainLayout>
          <ProtectedRoute>
            <UserManagementPage />
          </ProtectedRoute>
        </MainLayout>
      }
    />
  </Routes>
);

const App = () => (
  <I18nextProvider i18n={i18n}>
    <Provider store={store}>
      <ThemeProvider theme={default_theme}>
        <CssBaseline />
        <GlobalStylesComponent />
        <Router>
          <AppInitializer>
            <AppRoutes />
          </AppInitializer>
        </Router>
      </ThemeProvider>
    </Provider>
  </I18nextProvider>
);

export default App;